import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import RichTextEditor from "react-rte";
import { message } from "../../Reducers/loading/actions";

function AIEnhancedRichText({ input, label, error, touched, warning, dispatch }) {
  const [value, setValue] = useState(RichTextEditor.createEmptyValue());
  const [markdownValue, setMarkdownValue] = useState("");
  const [isRewriting, setIsRewriting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState("");

  useEffect(() => {
    if (input.value && markdownValue.length < 3) {
      setValue(input.value === "" ? RichTextEditor.createEmptyValue() : RichTextEditor.createValueFromString(input.value, "html"));
    }
  }, [input.value, markdownValue.length]);

  const onChange = (newValue) => {
    setValue(newValue);
    setMarkdownValue(newValue.toString("markdown"));
    if (newValue.toString("markdown").length > 3) {
      input.onChange(newValue.toString("html"));
    }
  };

  const handleAIRewrite = async () => {
    const currentText = value.toString("html");
    
    if (!currentText || currentText.trim() === "" || currentText === "<p><br></p>") {
      dispatch(message(3000, "warning", "Voer eerst tekst in voordat je deze kunt laten herschrijven door AI"));
      return;
    }

    setIsRewriting(true);
    
    try {
      const response = await fetch("/api/admin/cms/ai/herschrijf-tekst", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token"),
        },
        body: JSON.stringify({
          text: currentText
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setRewrittenContent(result.rewritten);
        setShowPreview(true);
        dispatch(message(3000, "success", "Tekst succesvol herschreven met AI! Bekijk het voorstel hieronder."));
      } else {
        throw new Error(result.error || "Onbekende fout bij AI herschrijving");
      }
    } catch (error) {
      console.error("AI Rewrite Error:", error);
      dispatch(message(4000, "error", error.message || "Er is een fout opgetreden bij het herschrijven van de tekst"));
    } finally {
      setIsRewriting(false);
    }
  };

  const acceptRewrite = () => {
    const newValue = RichTextEditor.createValueFromString(rewrittenContent, "html");
    setValue(newValue);
    setMarkdownValue(newValue.toString("markdown"));
    input.onChange(rewrittenContent);
    setShowPreview(false);
    setRewrittenContent("");
    dispatch(message(3000, "success", "AI voorstel geaccepteerd en toegepast!"));
  };

  const rejectRewrite = () => {
    setShowPreview(false);
    setRewrittenContent("");
    dispatch(message(2000, "info", "AI voorstel geweigerd. Originele tekst behouden."));
  };

  return (
    <div className="ai-enhanced-richtext">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-semibold">{label}</label>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={handleAIRewrite}
          disabled={isRewriting}
          title="Herschrijf de tekst met AI op basis van de contentstijl-gids"
        >
          {isRewriting ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Herschrijven...</span>
              </div>
              AI bezig...
            </>
          ) : (
            <>
              <i className="fas fa-robot me-2"></i>
              AI Herschrijven
            </>
          )}
        </button>
      </div>
      
      <div className="richtext-container">
        <RichTextEditor 
          className="wysiwyg-editor ai-enhanced-editor" 
          value={value} 
          onChange={onChange}
          placeholder="Voer hier uw tekst in..."
        />
        {touched && ((error && <span className="text-danger mt-1 d-block">{error}</span>) || 
          (warning && <span className="text-warning mt-1 d-block">{warning}</span>))}
      </div>

      {showPreview && (
        <div className="ai-preview-container mt-3">
          <div className="card border-success">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <i className="fas fa-magic text-success me-2"></i>
                <strong className="text-success">AI Voorstel</strong>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={acceptRewrite}
                  title="Accepteer het AI voorstel"
                >
                  <i className="fas fa-check me-1"></i>
                  Accepteren
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={rejectRewrite}
                  title="Weiger het AI voorstel"
                >
                  <i className="fas fa-times me-1"></i>
                  Weigeren
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="alert alert-info mb-3">
                <small>
                  <i className="fas fa-info-circle me-1"></i>
                  Deze tekst is herschreven volgens uw AI contentstijl-gids instellingen. 
                  Bekijk het resultaat en kies of u het wilt accepteren of behouden.
                </small>
              </div>
              <div className="preview-content border rounded p-3 bg-white">
                <div dangerouslySetInnerHTML={{ __html: rewrittenContent }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .ai-enhanced-richtext {
          position: relative;
        }
        
        .ai-enhanced-editor {
          border-radius: 0.75rem;
          border: 1px solid rgba(221, 227, 192, 0.5);
        }
        
        .ai-enhanced-editor:focus-within {
          border-color: #B1BF41;
          box-shadow: 0 0 0 3px rgba(177, 191, 65, 0.1);
        }
        
        .btn-outline-primary {
          color: #B1BF41;
          border-color: #B1BF41;
          font-size: 0.875rem;
          padding: 0.375rem 0.75rem;
        }
        
        .btn-outline-primary:hover {
          background: #B1BF41;
          border-color: #B1BF41;
          color: white;
        }
        
        .btn-outline-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .ai-preview-container .card {
          border-radius: 0.75rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .ai-preview-container .card-header {
          border-radius: 0.75rem 0.75rem 0 0;
        }
        
        .preview-content {
          max-height: 300px;
          overflow-y: auto;
        }
        
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }
      `}</style>
    </div>
  );
}

export default connect()(AIEnhancedRichText);